import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Clock, User, Tag, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`).then(r => { setPost(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen pt-32 pb-24">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 space-y-8 animate-pulse">
        <div className="h-6 bg-bb-rich w-1/4" /><div className="h-12 bg-bb-rich w-3/4" /><div className="aspect-video bg-bb-rich" />
      </div>
    </div>
  );

  if (!post) return (
    <div className="min-h-screen pt-32 pb-24 text-center">
      <h1 className="font-heading text-3xl c-cream">Article not found</h1>
      <Link to="/blog" className="t-label c-gold mt-4 inline-block">Back to Journal</Link>
    </div>
  );

  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let listItems = [];
    let inList = false;

    const flush = (key) => {
      if (listItems.length > 0) {
        elements.push(<ul key={key} className="space-y-2 mb-6 ml-4">{listItems}</ul>);
        listItems = [];
        inList = false;
      }
    };

    lines.forEach((line, i) => {
      if (line.startsWith('## ')) {
        flush(`ul-${i}`);
        elements.push(<h2 key={i} className="font-heading text-2xl c-cream mt-10 mb-4">{line.replace('## ', '')}</h2>);
      } else if (line.startsWith('### ')) {
        flush(`ul-${i}`);
        elements.push(<h3 key={i} className="font-heading text-xl c-cream italic mt-8 mb-3">{line.replace('### ', '')}</h3>);
      } else if (line.startsWith('- ')) {
        inList = true;
        const text = line.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '<strong class="c-cream font-medium">$1</strong>');
        listItems.push(<li key={`item-${i}`} className="t-body text-sm c-muted pl-5 relative"><span className="absolute left-0 top-2.5 w-1.5 h-1.5 bg-bb-gold rounded-full" /><span dangerouslySetInnerHTML={{ __html: text }} /></li>);
      } else if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) {
        flush(`ul-${i}`);
        elements.push(<p key={i} className="font-heading text-lg c-gold italic my-6">{line.replace(/\*/g, '')}</p>);
      } else if (line.trim() !== '') {
        flush(`ul-${i}`);
        const text = line.replace(/\*\*(.*?)\*\*/g, '<strong class="c-cream font-medium">$1</strong>');
        elements.push(<p key={i} className="t-body c-muted text-sm leading-loose mb-4" dangerouslySetInnerHTML={{ __html: text }} />);
      }
    });
    flush('final');
    return elements;
  };

  return (
    <div className="min-h-screen pt-32" data-testid="blog-post-page">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 mb-10">
        <Link to="/blog" className="t-label c-muted hover:c-cream transition-colors inline-flex items-center gap-2" data-testid="back-to-blog">
          <ChevronLeft className="w-3 h-3" /> Back to Journal
        </Link>
      </div>

      <article className="max-w-4xl mx-auto px-6 lg:px-12 pb-24">
        <header className="mb-10">
          <span className="t-label c-gold block mb-4">{post.category}</span>
          <h1 className="t-headline text-4xl md:text-5xl c-cream mb-6 leading-tight">{post.title}</h1>
          <div className="flex flex-wrap gap-6 t-label c-muted text-[10px]">
            <span className="flex items-center gap-2"><User className="w-3 h-3" /> {post.author}</span>
            <span className="flex items-center gap-2"><Clock className="w-3 h-3" /> 5 min read</span>
          </div>
          <div className="gold-line mt-8" />
        </header>

        <div className="aspect-video overflow-hidden border border-bb-border mb-14">
          <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="max-w-3xl mx-auto">
          {renderContent(post.content)}
        </div>

        <div className="max-w-3xl mx-auto mt-14 pt-6 border-t border-bb-border">
          <div className="flex items-center gap-2 t-label c-muted text-[10px] mb-4"><Tag className="w-3 h-3" /> Tags</div>
          <div className="flex flex-wrap gap-2">
            {post.tags.map(tag => (
              <span key={tag} className="border border-bb-dim px-3 py-1.5 t-label text-[10px] c-muted hover:border-bb-gold hover:c-gold transition-colors" data-testid={`tag-${tag}`}>{tag}</span>
            ))}
          </div>
        </div>

        <div className="max-w-3xl mx-auto mt-14 bg-bb-rich border border-bb-border p-10 text-center">
          <h3 className="font-heading text-2xl c-cream mb-3">Ready to Experience These Benefits?</h3>
          <p className="t-body c-muted text-sm mb-6">Shop our premium bee products sourced from California and Arizona.</p>
          <Link to="/products"><button className="btn-primary" data-testid="blog-shop-cta">Shop the Collection</button></Link>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
